import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { CreditCard, ExternalLink, Loader2, X } from "lucide-react";
import { motion } from "framer-motion";
import { useUserPlan } from "../../planes/hooks/useUserPlan";
import type UserPlan from "../../planes/models/UserPlan";
import type { UserProfile } from "../models/UserProfile";
import GlassContainer from "@/shared/components/glasscontainer";

interface UserPlanOverviewProps {
  user: UserProfile;
  onClose: () => void;
}

const STATUS_CONFIG: Record<number, { label: string; className: string }> = {
  0: {
    label: "No Pagado",
    className: "bg-red-500/20 text-red-200 border-red-500/30",
  },
  1: {
    label: "Cancelado",
    className: "bg-gray-500/20 text-gray-300 border-gray-500/30",
  },
  2: {
    label: "Pagado",
    className: "bg-green-500/20 text-green-200 border-green-500/30",
  },
};

function formatDuration(plan: UserPlan["planNavigation"]): string {
  const u = plan.timeUnits;
  if (u.years > 0) return `${u.years} año${u.years > 1 ? "s" : ""}`;
  if (u.months > 0) return `${u.months} mes${u.months > 1 ? "es" : ""}`;
  if (u.days > 0) return `${u.days} día${u.days > 1 ? "s" : ""}`;
  if (u.hours > 0) return `${u.hours} hora${u.hours > 1 ? "s" : ""}`;
  if (u.minutes > 0) return `${u.minutes} minuto${u.minutes > 1 ? "s" : ""}`;
  return "—";
}

export function UserPlanOverview({ user, onClose }: UserPlanOverviewProps) {
  const navigate = useNavigate();
  const { getUserPlan, loading } = useUserPlan();
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    setFetched(false);
    setUserPlan(null);
    const fetchPlan = async () => {
      const plan = await getUserPlan(user.id);
      setUserPlan(plan ?? null);
      setFetched(true);
    };
    fetchPlan();
  }, [user.id, getUserPlan]);

  const avatarText = (user.name || user.email).substring(0, 2).toUpperCase();
  const statusConfig =
    userPlan != null
      ? (STATUS_CONFIG[userPlan.status] ?? STATUS_CONFIG[1])
      : null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 50, scale: 0.98 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 50, scale: 0.98 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="absolute right-4 top-4 bottom-4 w-80 z-20 flex flex-col gap-5 overflow-auto border border-white/10 bg-zinc-900/90 backdrop-blur-xl p-6 rounded-3xl shadow-2xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest">
          Resumen
        </p>
        <button
          onClick={onClose}
          className="p-1.5 cursor-pointer rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-white/10 transition-all"
        >
          <X className="size-4" />
        </button>
      </div>

      {/* User Info */}
      <GlassContainer className="p-5 rounded-2xl">
        <div className="flex items-center gap-3">
          <div
            className="flex size-12 shrink-0 items-center justify-center rounded-xl font-bold text-sm"
            style={{ backgroundColor: "#3b82f625", color: "#60a5fa" }}
          >
            {avatarText}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-zinc-100 truncate">
              {user.name || "Sin nombre"}
            </p>
            <p className="text-xs text-zinc-400 truncate">{user.email}</p>
          </div>
        </div>
      </GlassContainer>

      {/* Plan section */}
      <div className="flex-1">
        <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mb-3">
          Plan actual
        </p>

        {loading && !fetched && (
          <div className="flex justify-center py-10">
            <Loader2 className="size-6 animate-spin text-accent" />
          </div>
        )}

        {fetched && !userPlan && (
          <GlassContainer className="p-5 rounded-2xl flex flex-col items-center gap-3 text-center">
            <CreditCard className="size-8 text-zinc-500" />
            <p className="text-sm text-zinc-400 font-medium">
              Sin plan asignado
            </p>
            <button
              onClick={() => navigate(`/admin/accounts/${user.id}/plan`)}
              className="text-sm font-semibold text-blue-400 hover:text-blue-300 hover:underline transition-all"
            >
              Asignar plan
            </button>
          </GlassContainer>
        )}

        {fetched && userPlan && (
          <GlassContainer className="p-5 rounded-2xl space-y-4">
            {/* Plan title + status */}
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-bold text-zinc-100 truncate">
                  {userPlan.planNavigation.title || "Sin título"}
                </p>
                <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">
                  {userPlan.planNavigation.description || "—"}
                </p>
              </div>
              {statusConfig && (
                <span
                  className={`shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full border ${statusConfig.className}`}
                >
                  {statusConfig.label}
                </span>
              )}
            </div>

            {/* Price + Duration */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1">
                  Precio
                </p>
                <p className="font-bold text-zinc-100">
                  {userPlan.planNavigation.price.toFixed(2)}{" "}
                  <span className="text-[10px] font-medium text-zinc-400">
                    US$
                  </span>
                </p>
              </div>
              <div className="bg-white/5 rounded-xl p-3 border border-white/10">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1">
                  Duración
                </p>
                <p className="font-bold text-zinc-100 text-sm">
                  {formatDuration(userPlan.planNavigation)}
                </p>
              </div>
            </div>

            {/* Payment status */}
            <div>
              <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-2">
                Estado de pago
              </p>
              <span
                className={`inline-flex text-[11px] font-bold px-3 py-1 rounded-full border ${statusConfig?.className}`}
              >
                {statusConfig?.label}
              </span>
            </div>
          </GlassContainer>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2">
        <button
          onClick={() => navigate(`/admin/accounts/${user.id}/plan`)}
          className="w-full cursor-pointer flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 font-semibold text-sm transition-all border border-blue-500/30"
        >
          <CreditCard className="size-4" />
          {userPlan ? "Gestionar plan" : "Asignar plan"}
        </button>
        <button
          onClick={() => navigate(`/admin/accounts/${user.id}`)}
          className="w-full cursor-pointer flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl hover:bg-white/10 text-zinc-300 font-semibold text-sm transition-all border border-white/10"
        >
          <ExternalLink className="size-4" />
          Ver perfil
        </button>
      </div>
    </motion.div>
  );
}
